import { apiClient } from "@/lib/api/client"
import type {
  VisitorQuoteRequest,
  CreateIndividualQuotePayload,
  CreateCorporateQuotePayload,
  GeneratedQuote,
  TierOptionsResponse,
  UpdateQuoteSelectionsPayload,
  PatchQuoteStatusResponse,
  MergeContactedResponse,
} from "./types"

export async function createIndividualQuoteApi(
  payload: CreateIndividualQuotePayload
): Promise<VisitorQuoteRequest> {
  const res = await apiClient.post("/visitor-quotes/individual", payload)
  return (res.data ?? res) as unknown as VisitorQuoteRequest
}

export async function createCorporateQuoteApi(
  payload: CreateCorporateQuotePayload
): Promise<VisitorQuoteRequest> {
  // Build FormData for multipart/form-data upload (supports file attachment)
  const formData = new FormData()

  // Append JSON fields
  formData.append("quote_type", payload.quote_type)
  formData.append("corporate_detail", JSON.stringify(payload.corporate_detail))
  formData.append("census_members", JSON.stringify(payload.census_members))
  formData.append("contact", JSON.stringify(payload.contact))

  // Append file if provided
  if (payload.file) {
    formData.append("file", payload.file)
  }

  const res = await apiClient.post("/visitor-quotes/corporate", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
  return (res.data ?? res) as unknown as VisitorQuoteRequest
}

export async function getVisitorQuoteByIdApi(
  id: string
): Promise<VisitorQuoteRequest> {
  const res = await apiClient.get(`/visitor-quotes/${id}`)
  return (res.data ?? res) as unknown as VisitorQuoteRequest
}

export async function getVisitorQuotesByIdApi(
  id: string,
  benefitCategory?: string
): Promise<{ success: boolean; count: number; data: GeneratedQuote[] }> {
  const params = benefitCategory ? { benefit_category: benefitCategory } : {}
  const res = await apiClient.get(`/visitor-quotes/${id}/quotes`, { params })
  return (res.data ?? res) as unknown as {
    success: boolean
    count: number
    data: GeneratedQuote[]
  }
}

export async function fetchCategoriesApi(
  id: string
): Promise<{ categories: string[] }> {
  const res = await apiClient.get(`/visitor-quotes/${id}/categories`)
  return (res.data ?? res) as unknown as { categories: string[] }
}

export async function deleteVisitorQuoteApi(id: string): Promise<void> {
  await apiClient.delete(`/visitor-quotes/${id}`)
}

export async function getTierOptionsApi(
  productCode: string,
  tierId: string,
  visaEmirate?: string
): Promise<TierOptionsResponse> {
  const qs = visaEmirate
    ? `?visaEmirate=${encodeURIComponent(visaEmirate)}`
    : ""
  const res = await apiClient.get(
    `/pricing/products/${productCode}/tiers/${tierId}/options${qs}`
  )
  return (res.data ?? res) as unknown as TierOptionsResponse
}

export async function updateQuoteSelectionsApi(
  quoteId: string,
  payload: UpdateQuoteSelectionsPayload
): Promise<unknown> {
  const res = await apiClient.patch(`/quotes/${quoteId}`, payload)
  return res.data ?? res
}

export async function patchQuoteStatusApi(
  quoteId: string,
  payload: { status: string; benefit_category?: string }
): Promise<PatchQuoteStatusResponse> {
  const res = await apiClient.patch(
    `/visitor-quotes/${quoteId}/status`,
    payload
  )
  return (res.data ?? res) as unknown as PatchQuoteStatusResponse
}

export async function resetVisitorQuotesApi(
  visitorId: string
): Promise<unknown> {
  const res = await apiClient.post(`/visitor-quotes/${visitorId}/reset`, {})
  return res.data ?? res
}

export async function fetchContactedSummaryApi(
  visitorId: string
): Promise<import("./types").ContactedSummaryResponse> {
  const res = await apiClient.get(
    `/visitor-quotes/${visitorId}/contacted-summary`
  )
  return (res.data ??
    res) as unknown as import("./types").ContactedSummaryResponse
}

export async function mergeContactedApi(
  visitorId: string
): Promise<MergeContactedResponse> {
  const res = await apiClient.post(
    `/visitor-quotes/${visitorId}/merge-contacted`
  )
  return (res.data ?? res) as unknown as MergeContactedResponse
}

export async function updateVisitorPricingOverridesApi(
  visitorId: string,
  payload: {
    nova_fee_percentage?: number
    broker_fee_percentage?: number
    discount_percentage?: number
    note?: string
  }
): Promise<any> {
  const res = await apiClient.patch(
    `/quotes/visitor/${visitorId}/pricing-overrides`,
    payload
  )
  return res.data ?? res
}
